import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { finalize, map, mergeMap, switchMap, tap } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { StorageService } from '../utils/storage.service';
import { User, UserType } from '../shared/types';
import { BuyerConfigService } from './buyer-config/buyer-config.service';
import {
  GetBuyerConfigResponse,
  BuyerConfig,
  AllBuyerConfigSettings,
} from './buyer-config/buyer-config.service.interface';

import {
  AuthFormData,
  LoginResponse,
  ForgotPasswordResponse,
  TokenDataResponse,
  ResetFormData,
  ResetPasswordResponse,
  ForgotFormData,
  AuthState,
  CheckAccessResponse,
  ServiceBuyer,
  MultiBuyerCmpAuth,
  ExchangeHandoverTokenResponse,
} from './auth.interface';
import { CompanyService } from '../shared/services/company/company.service';
import { CURRENCY_CODE, UNITS } from '../constants/currency-units';
import {
  CompanyResponse,
  CompanyResponseObject,
} from '../shared/interfaces/company.interface';
import { AuthState as AuthStateInterface } from '@purespectrum1/ui/marketplace/shared/interfaces/auth.interface';
import {
  AppcuesService,
  ChurnZeroConsts,
  ChurnZeroService,
  NEWRELIC,
  Newrelic,
  AppcuesConsts,
} from '@purespectrum1/ui/tracking';
import { CurrencyService } from '../shared/services/currency-service';
import { CurrencyExchange } from '../shared/types/currency-service-interface';
import Intercom, { shutdown } from '@intercom/messenger-js-sdk';
import { ServiceOperatorsLevels } from '../constants/global';
import { MultiBuyerRole } from '../shared/services/multi-buyer/interfaces/multi-buyer-company.interface';
import { MultiBuyerStateService } from '../shared/services/multi-buyer/multi-buyer-state.service';
import { FullStory } from '@fullstory/browser';
import { AccessConfig } from '../shared/services/multi-buyer/access-config/access-config';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _baseUrl = environment.pureSpectrum.url;
  private readonly _storageKey = 'authStateStorage';

  /** Flag to persist the auth info to localstorage */
  private _rememberUser = false;

  private _buyerConfig = {
    _id: '',
    advanceTarget: false,
    cmp: false,
    overridCmp: false,
    buyerClient: false,
    enableUrlTranfrmUsr: false,
    decipher: false,
    recontact: false,
    maxinProgress: false,
    bera: false,
    supplierAccordian: false,
    incl_excl: false,
    clickBal: false,
    includeToo: false,
    uniqBuyerEntryLink: false,
    qbp: false,
    qlc: false,
    quotaThrottling: false,
    reconciliations: false,
    saveReconciliationOriginalFile: false,
    enableFeot: false,
    enableHyperTarget: false,
    enableScheduledLaunch: false,
    enableSurveyExternalId: false,
    enableNightcrawler: false,
    enableDataQualityQues: false,
    advanceStats: false,
    enableQuestionLibrary: false,
    enableTrafficChannels: false,
    enableInvoiceTab: false,
    enablePriceRecomendation: false,
    enableSupplierGrouping: false,
    enableMarginMax: false,
    enableClickToCalculate: false,
    enableAutoFieldManagement: false,
    enableMultiCountry: false,
    enableManualProcessing: false,
    enableTargetMargin: false,
    enableTargetMarginLaunchAll: false,
    enableTemplate: false,
    enableExtraCoreQualification: false,
    enablePureScoreAndRD: false,
    enableViewByPM: false,
    enableDynamicDash: false,
    enableSurveyDQBattery: false,
    enableNestingQuotaGrouping: false,
    enableAdvanceFeasibilityDropDown: false,
    enableBasicToken: false,
    enablePurePriceRates: false,
    enableDynamicReports: false,
    enableSurveyDetailsReport: false,
    enableHealthMetrics: false,
    enableProjectLevelDataQuality: false,
    enableDefaultSettingsPureScore: false,
    enableSettingsFpDupeCheck: false,
    enableSettingsFpFraudCheck: false,
    enableDataQualityControls: false,
    enableMandatoryBillingNumber: false,
    enableBillingNumberRules: false,
    enableFilteringSortingBulkEdit: false,
    enableTransactionTestingTool: false,
    enablePMRequired: false,
    enableMultiCountrySingleProjectView: false,
    enableSegmentationClass: false,
    enableClassicSTR: false,
    enableModularSurvey: false,
    enablePaceTool: false,
    enableCreateSurveyV2: false,
    enableSimilarSurveys: false,
    enableSearchAllMarketPlaceInventory: false,
    enableSupplierAllocationThrottling: false,
    enableRYGSurveyScore: false,
    enableIntercomApp: false,
    enableAiDeliveryAssistance: false,
    enableNestingMultiQuota: false,
    enableRespondentStatusFilter: false,
    enableScheduledPause: false,
    enablePriceOverrideFeasibilityMultiplier: false,
    enableTracker: false,
    enableBypassLOIThreshold: false,
    enableBackEndSortFilter: false,
    enableCustomSupplierAllocationDefault: false,
    enableRangeQuotasDistribution: false,
    enableFMAStatusBasedAutoPause: false,
    customExclusionPeriod: false,
    enablePermanentExclusion: false,
    setPermanentExclusionOffByDefault: false,
    enableUiSwitcher: false,
  };
  private _allBuyerConfigSettings: AllBuyerConfigSettings = {
    advanceTarget: [],
    cmp: [],
    overridCmp: [],
    buyerClient: [],
    enableUrlTranfrmUsr: [],
    decipher: [],
    recontact: [],
    maxinProgress: [],
    bera: [],
    supplierAccordian: [],
    incl_excl: [],
    clickBal: [],
    includeToo: [],
    uniqBuyerEntryLink: [],
    qbp: [],
    qlc: [],
    quotaThrottling: [],
    reconciliations: [],
    saveReconciliationOriginalFile: [],
    enableFeot: [],
    enableHyperTarget: [],
    enableScheduledLaunch: [],
    enableSurveyExternalId: [],
    enableNightcrawler: [],
    enableDataQualityQues: [],
    enableSurveyDQBattery: [],
    advanceStats: [],
    enableQuestionLibrary: [],
    enableTrafficChannels: [],
    enableInvoiceTab: [],
    enablePriceRecomendation: [],
    enableSupplierGrouping: [],
    enableMarginMax: [],
    enableClickToCalculate: [],
    enableAutoFieldManagement: [],
    enableMultiCountry: [],
    enableManualProcessing: [],
    enablePureScoreAndRD: [],
    enableViewByPM: [],
    enableBasicToken: [],
    enablePurePriceRates: [],
    enableBillingNumberRules: [],
    enableMandatoryBillingNumber: [],
    enableTransactionTestingTool: [],
    enablePMRequired: [],
    enableMultiCountrySingleProjectView: [],
    enableSegmentationClass: [],
    enableClassicSTR: [],
    enablePaceTool: [],
    enableCreateSurveyV2: [],
    enableSimilarSurveys: [],
    enableSearchAllMarketPlaceInventory: [],
    enableSupplierAllocationThrottling: [],
    enableRYGSurveyScore: [],
    enableIntercomApp: [],
    enableAiDeliveryAssistance: [],
    enableNestingQuotaGrouping: [],
    enableNestingMultiQuota: [],
    enableRespondentStatusFilter: [],
    enableScheduledPause: [],
    enablePriceOverrideFeasibilityMultiplier: [],
    enableTracker: [],
    enableBypassLOIThreshold: [],
    enableBackEndSortFilter: [],
    enableCustomSupplierAllocationDefault: [],
    enableRangeQuotasDistribution: [],
    enableFMAStatusBasedAutoPause: [],
    customExclusionPeriod: [],
    enablePermanentExclusion: [],
    setPermanentExclusionOffByDefault: [],
  };
  private _companyConfig = {
    fx: CURRENCY_CODE.US,
  };

  private readonly _defaultCurrencyExchange = {} as CurrencyExchange;
  private _currencyExchange = this._defaultCurrencyExchange;

  private readonly _defaultState: Readonly<AuthState> = {
    token: undefined,
    user: undefined,
    userType: 'none',
    buyerConfig: this._buyerConfig,
    companyConfig: this._companyConfig as CompanyResponseObject,
    rememberUser: false,
    churnZeroKey: '',
    serviceBuyer: undefined,
    selectedMultiBuyerCompany: undefined,
    allBuyerConfigs: this._allBuyerConfigSettings,
    currencyExchange: this._currencyExchange,
    accessControlConfig: AccessConfig.uninitialized(),
  };

  private _state: Readonly<AuthState> = { ...this._defaultState };

  // Allow subscribing to changes in the auth status
  private _loggedIn$ = new BehaviorSubject<boolean>(false);
  loggedIn$ = this._loggedIn$.asObservable();

  private _loggedInAsServiceBuyer$ = new BehaviorSubject<boolean>(false);
  loggedInAsServiceBuyer$ = this._loggedInAsServiceBuyer$.asObservable();

  private _buyerConfig$ = new BehaviorSubject<BuyerConfig>(
    this._state.buyerConfig!
  );
  public buyerConfig$ = this._buyerConfig$.asObservable();

  get token() {
    return this._state.token;
  }

  get user() {
    return this._state.user;
  }

  get userType() {
    return this._state.userType;
  }

  get buyerConfig() {
    return this._state.buyerConfig;
  }

  get allBuyerConfigs() {
    return this._state.allBuyerConfigs;
  }

  get companyConfig() {
    return {
      ...this._state.companyConfig,
      currencySymbol: UNITS[this._state.companyConfig?.fx || CURRENCY_CODE.US],
    };
  }

  get churnZeroKey() {
    return this._state.churnZeroKey;
  }

  get serviceBuyer() {
    return this._state.serviceBuyer;
  }

  get userCompanyId() {
    return this._state.user?.cmp;
  }

  get currencyExchange() {
    return this._state.currencyExchange;
  }

  constructor(
    private _http: HttpClient,
    private _storageService: StorageService,
    private _buyerConfigService: BuyerConfigService,
    private _companyService: CompanyService,
    private _churnZeroService: ChurnZeroService,
    private _currencyService: CurrencyService,
    private _appcuesService: AppcuesService,
    private _multiBuyerState: MultiBuyerStateService,
    @Optional() @Inject(NEWRELIC) private _newrelic?: Newrelic
  ) {
    const previousAuth = this._storageService.getItem<AuthState>(
      this._storageKey
    );
    if (previousAuth) {
      this._rememberUser = previousAuth.rememberUser;
      // We were previously storing the TOS state under a separate item, now it's consolidated under
      // the user object, but we don't want to display TOS again to people that have already accepted it
      const tosAccepted = this._storageService.getItem<boolean>('tosAccepted');
      this._storageService.removeItem('tosAccepted');

      if (tosAccepted && previousAuth.user) {
        previousAuth.user.tos = previousAuth.user.tos || {};
        previousAuth.user.tos.accept = true;
      }

      const restoredConfig = previousAuth.accessControlConfig
        ? AccessConfig.fromApiResponse(previousAuth.accessControlConfig)
        : AccessConfig.default();
      this._setState({ ...previousAuth, accessControlConfig: restoredConfig });
      this._startIntercom();
    }

    this._multiBuyerState.setAccessControlConfig(
      this._state.accessControlConfig
    );
  }

  getAccessControlConfig(clientId: number, userRole: MultiBuyerRole) {
    return this._multiBuyerState.getAccessControlConfig(clientId, userRole);
  }

  resolveAccessControlConfig(): Observable<AccessConfig> {
    const current = this._multiBuyerState.accessControlConfig;
    if (current.isReady) {
      return of(current);
    }

    const user = this._state.user;
    if (!user) {
      const defaultConfig = AccessConfig.default();
      this._setState({ accessControlConfig: defaultConfig });
      return of(defaultConfig);
    }

    const userRole = this.getMultiBuyerRole(user);
    if (!userRole) {
      const defaultConfig = AccessConfig.default();
      this._setState({ accessControlConfig: defaultConfig });
      return of(defaultConfig);
    }

    return this.getAccessControlConfig(user.cmp, userRole).pipe(
      map((apiConfig) => AccessConfig.fromApiResponse(apiConfig)),
      tap((cfg) => this._setState({ accessControlConfig: cfg }))
    );
  }

  /**
   * Authenticate the user and handle saving the auth token if rememberme is set.
   */
  login(authData: AuthFormData) {
    return this._http
      .post<LoginResponse>(this._baseUrl + '/user/login', null, {
        headers: {
          skipInterceptor: 'true',
          username: authData.email,
          password: authData.password,
          captcha: authData?.captcha || '',
        },
      })
      .pipe(
        mergeMap((response): any => {
          this._rememberUser = !!authData.rememberme;
          const userType = this.getUserType(response.user);
          this._setFullStoryIdentity(response);
          this._setState({
            token: response.token,
            user: response.user,
            userType: userType,
            rememberUser: !!authData.rememberme,
            churnZeroKey: response.churnZeroKey,
          });
          this._churnZeroService.sendEvent(
            ChurnZeroConsts.events.login,
            ChurnZeroConsts.eventDescriptions.login
          );
          this._appcuesService.sendEvent(AppcuesConsts.events.login, {
            description: AppcuesConsts.eventDescriptions.login,
          });
          this._newrelic?.setUserId(`${response.user.id}`);

          const companyConfig$ = this._companyService
            .getCompany(response.user.cmp)
            .pipe(map((companyResponse) => companyResponse.company.pop()));

          const buyerConfig$ = this._buyerConfigService.getBuyerConfig().pipe(
            map((responseConfig: GetBuyerConfigResponse) => {
              const configSetting: BuyerConfig = this._getConfigSetting(
                responseConfig,
                response.user.cmp
              );

              const allBuyerConfigs: AllBuyerConfigSettings =
                this._getAllBuyerConfigSettings(
                  responseConfig,
                  this.getUserType(response.user)
                );

              return {
                buyerConfig: configSetting,
                allBuyerConfigs: allBuyerConfigs,
              };
            })
          );

          const userRole = this.getMultiBuyerRole(response.user);

          const accessConfig$ = userRole
            ? this.getAccessControlConfig(response.user.cmp, userRole).pipe(
                map((apiConfig) => AccessConfig.fromApiResponse(apiConfig))
              )
            : of(AccessConfig.default());

          return forkJoin({
            config: buyerConfig$,
            companyConfig: companyConfig$,
            accessConfig: accessConfig$,
          }).pipe(
            tap(({ config, companyConfig, accessConfig }) => {
              this._setState({
                buyerConfig: config.buyerConfig,
                allBuyerConfigs: config.allBuyerConfigs,
                companyConfig: companyConfig,
                accessControlConfig: accessConfig,
              });
              this._startIntercom();
            })
          );
        })
      );
  }

  /**
   * Exchanges a UI-switcher handover token (see handover.guard.ts) for a
   * session. Public endpoint, no token exists yet at this point.
   */
  exchangeHandoverToken(
    handoverToken: string
  ): Observable<ExchangeHandoverTokenResponse> {
    return this._http.post<ExchangeHandoverTokenResponse>(
      `${this._baseUrl}/auth/exchange-handover-token`,
      { handoverToken },
      { headers: { skipInterceptor: 'true' } }
    );
  }

  /**
   * Hydrates auth state from a handover session, skipping /user/login.
   * Mirrors login()'s post-authentication setup (buyerConfig/companyConfig/
   * access control) but is kept separate from login() itself so a handover
   * bug can't touch the primary login path.
   */
  hydrateFromHandover(
    session: { token: string; user: User },
    rememberUser: boolean = true
  ): Observable<void> {
    this._rememberUser = rememberUser;
    const userType = this.getUserType(session.user);

    this._setState({
      token: session.token,
      user: session.user,
      userType,
      rememberUser,
    });
    this._newrelic?.setUserId(`${session.user.id}`);

    const companyConfig$ = this._companyService
      .getCompany(session.user.cmp)
      .pipe(map((companyResponse) => companyResponse.company.pop()));

    const buyerConfig$ = this._buyerConfigService.getBuyerConfig().pipe(
      map((responseConfig: GetBuyerConfigResponse) => ({
        buyerConfig: this._getConfigSetting(responseConfig, session.user.cmp),
        allBuyerConfigs: this._getAllBuyerConfigSettings(
          responseConfig,
          userType
        ),
      }))
    );

    const userRole = this.getMultiBuyerRole(session.user);
    const accessConfig$ = userRole
      ? this.getAccessControlConfig(session.user.cmp, userRole).pipe(
          map((apiConfig) => AccessConfig.fromApiResponse(apiConfig))
        )
      : of(AccessConfig.default());

    return forkJoin({
      config: buyerConfig$,
      companyConfig: companyConfig$,
      accessConfig: accessConfig$,
    }).pipe(
      tap(({ config, companyConfig, accessConfig }) => {
        this._setState({
          buyerConfig: config.buyerConfig,
          allBuyerConfigs: config.allBuyerConfigs,
          companyConfig,
          accessControlConfig: accessConfig,
        });
        this._startIntercom();
      }),
      map(() => undefined)
    );
  }

  private _startIntercom() {
    if (!this.user) {
      return;
    }
    if (this._state.buyerConfig?.enableIntercomApp) {
      Intercom({
        app_id: 'edws7hvf',
        user_id: `${this.user.id}`,
        name: this.user.usrName,
        email: this.user.eml,
        last_login: Date.now(),
        company: {
          company_id: `${this.user.cmp}`,
          name: this.companyConfig.name,
        },
      });
    }
  }

  private _stopIntercom() {
    shutdown();
  }

  /**
   * Logout the active user and handles OIDC federated logout
   */
  logout() {
    const isOidc = !!(this.user as any)?.isOidc;
    const userId = this.user!.id;
    return this._http
      .delete<LoginResponse>(this._baseUrl + '/logout/' + userId)
      .pipe(
        switchMap(async () => {
          await this._appcuesService.logout();
        }),
        finalize(() => {
          this._setState(this._defaultState);
          this._storageService.clear();
          this._churnZeroService.disconnect();
          this._newrelic?.setUserId(null);
          this._stopIntercom();
          if (isOidc) {
            this._handleOidcSessionEnd();
          } else {
            // Reloading the window to disconnect sockets and load any updated files
            window.location.assign('/');
          }
        })
      );
  }

  /**
   * Clean up the Auth state for inactive/loggedout user
   */
  resetAuthState() {
    this._setState(this._defaultState);
    this._storageService.clear();
  }

  checkUserAccess() {
    const paramOptions = {
      buyer: this._state.user?.buyerAcssLvls || 'none',
      operator: this._state.user?.operatorAcssLvls || 'none',
      supplier: this._state.user?.supplierAcssLvls || 'none',
    };
    let params = new HttpParams({ fromObject: paramOptions });
    return this._http.get<CheckAccessResponse>(
      `${this._baseUrl}/feature/access`,
      { params }
    );
  }

  forgotPassword(forgotPassData: ForgotFormData) {
    return this._http.get<ForgotPasswordResponse>(
      `${this._baseUrl}/checkForgotPasswordMail/${forgotPassData.email}`,
      {
        headers: {
          skipInterceptor: 'true',
          captcha: forgotPassData?.captcha || '',
        },
      }
    );
  }

  getUserDataFromResetToken(resetToken: string) {
    return this._http.get<TokenDataResponse>(
      `${this._baseUrl}/userDataFromResetToken/${resetToken}`,
      {
        headers: {
          skipInterceptor: 'true',
        },
      }
    );
  }

  resetPassword(resetToken: string, resetPassData: ResetFormData) {
    return this._http.put<ResetPasswordResponse>(
      `${this._baseUrl}/updateuserresetpassword/${resetToken}`,
      resetPassData,
      {
        headers: {
          skipInterceptor: 'true',
        },
      }
    );
  }

  /**
   * Check if the user is currently logged in
   */
  isLoggedIn(): boolean {
    return !!this._state.token;
  }

  isLoggedInAsServiceBuyer(): boolean {
    return !!this._state.serviceBuyer;
  }

  isLoggedInAsMultiBuyer(): boolean {
    return this._multiBuyerState.isLoggedInAsMultiBuyer();
  }

  isLoggedInAsMasterMultiBuyer(): boolean {
    return this._multiBuyerState.isLoggedInAsMasterMultiBuyer();
  }

  isLoggedInAsMbManager(): boolean {
    return this._multiBuyerState.isLoggedInAsMbManager();
  }

  isSelectedMultiBuyerCompany(): boolean {
    return this._multiBuyerState.isSelectedMultiBuyerCompany();
  }

  getMultiBuyerRole(user: User): MultiBuyerRole | undefined {
    if (user.buyerAcssLvls === MultiBuyerRole.MasterMultiBuyer) {
      return MultiBuyerRole.MasterMultiBuyer;
    }
    if (user.buyerAcssLvls === MultiBuyerRole.MultiBuyer) {
      return user.isMBManager
        ? MultiBuyerRole.MbManager
        : MultiBuyerRole.MultiBuyer;
    }
    return undefined;
  }

  public hasServiceOperatorAccess(): boolean {
    return ServiceOperatorsLevels.includes(this.user?.operatorAcssLvls!);
  }

  getUserType(user: User): UserType {
    let userType: UserType = 'none';
    if (user.operatorAcssLvls !== 'none') {
      userType = 'operator';
    } else if (user.buyerAcssLvls !== 'none') {
      userType = 'buyer';
    } else if (user.supplierAcssLvls !== 'none') {
      userType = 'supplier';
    }
    return userType;
  }

  // TOS
  updateAcceptedTOS(userId: number, optIn: boolean) {
    const payload = [
      {
        op: 'replace',
        path: '/tos',
        value: true,
      },
      {
        op: 'replace',
        path: '/opt_in',
        value: optIn,
      },
    ];
    return this._http.patch(`${this._baseUrl}/users/${userId}`, payload).pipe(
      tap(() =>
        this._setState({
          user: {
            ...this.user!,
            tos: { accept: true, acceptTS: Date.now() },
          },
        })
      )
    );
  }

  saveBuyerConfig() {
    this._setState({
      buyerConfig: this.buyerConfig,
    });
  }

  setServiceBuyerData(serviceBuyer: ServiceBuyer) {
    this._updateBuyerConfigSettings(serviceBuyer.id);
    this._setState({
      serviceBuyer,
    });
  }

  setSelectedMultiBuyerCompanyData(
    selectedMultiBuyerCompany: MultiBuyerCmpAuth,
    buyerCmp: number
  ) {
    this._updateBuyerConfigSettings(buyerCmp);
    this._setState({ selectedMultiBuyerCompany });
  }

  resetServiceBuyerToken() {
    this._updateBuyerConfigSettings(this._state.user!.cmp);
    this._setState({
      serviceBuyer: undefined,
      currencyExchange: this._defaultCurrencyExchange,
    });
  }

  resetMultiBuyerToken() {
    const parentCompany =
      this._multiBuyerState.selectedMultiBuyerCompany?.parentCompany ??
      this._state.user!.cmp;
    this._updateBuyerConfigSettings(parentCompany);
    this._setState({
      user: { ...this._state.user!, cmp: parentCompany },
      selectedMultiBuyerCompany: undefined,
      currencyExchange: this._defaultCurrencyExchange,
    });
  }

  private _setState(newState: Partial<AuthState>) {
    this._state = { ...this._state, ...newState };
    this._storageService.setItem(this._storageKey, this._state, {
      useLocalStorage: this._rememberUser,
    });

    if ('buyerConfig' in newState) {
      this._buyerConfig$.next(this._state.buyerConfig!);
    }

    if ('selectedMultiBuyerCompany' in newState) {
      this._multiBuyerState.setSelectedCompany(
        this._state.selectedMultiBuyerCompany
      );
    }

    if ('accessControlConfig' in newState) {
      this._multiBuyerState.setAccessControlConfig(
        this._state.accessControlConfig
      );
    }

    this._loggedIn$.next(this.isLoggedIn());
    this._loggedInAsServiceBuyer$.next(this.isLoggedInAsServiceBuyer());
  }

  private _getConfigSetting(resposne: any, cmp: number): BuyerConfig {
    const data = resposne.data;
    const obj: any = { _id: data._id };
    for (let key in this._buyerConfig) {
      if (key === '_id') {
        continue;
      }
      if (data.hasOwnProperty(key) && data[key].includes(cmp)) {
        obj[key] = true;
      } else {
        obj[key] = false;
      }
    }
    return obj;
  }

  private _getAllBuyerConfigSettings(
    response: GetBuyerConfigResponse,
    userType: String
  ): AllBuyerConfigSettings {
    if (userType === 'operator') {
      return this._transformFieldsToAllBuyerConfig(response);
    }
    return this._allBuyerConfigSettings;
  }

  private _getFeatureFlag(featureFlag: Array<number> | undefined) {
    return featureFlag || [];
  }

  private _transformFieldsToAllBuyerConfig(
    response: GetBuyerConfigResponse
  ): AllBuyerConfigSettings {
    return {
      advanceTarget: this._getFeatureFlag(response.data.advanceTarget),
      cmp: this._getFeatureFlag(response.data.cmp),
      overridCmp: this._getFeatureFlag(response.data.overridCmp),
      buyerClient: this._getFeatureFlag(response.data.buyerClient),
      enableUrlTranfrmUsr: this._getFeatureFlag(
        response.data.enableUrlTranfrmUsr
      ),
      decipher: this._getFeatureFlag(response.data.decipher),
      recontact: this._getFeatureFlag(response.data.recontact),
      maxinProgress: this._getFeatureFlag(response.data.maxinProgress),
      bera: this._getFeatureFlag(response.data.bera),
      supplierAccordian: this._getFeatureFlag(response.data.supplierAccordian),
      incl_excl: this._getFeatureFlag(response.data.incl_excl),
      clickBal: this._getFeatureFlag(response.data.clickBal),
      includeToo: this._getFeatureFlag(response.data.includeToo),
      uniqBuyerEntryLink: this._getFeatureFlag(
        response.data.uniqBuyerEntryLink
      ),
      qbp: this._getFeatureFlag(response.data.qbp),
      qlc: this._getFeatureFlag(response.data.qlc),
      quotaThrottling: this._getFeatureFlag(response.data.quotaThrottling),
      reconciliations: this._getFeatureFlag(response.data.reconciliations),
      saveReconciliationOriginalFile: this._getFeatureFlag(
        response.data.saveReconciliationOriginalFile
      ),
      enableFeot: this._getFeatureFlag(response.data.enableFeot),
      enableHyperTarget: this._getFeatureFlag(response.data.enableHyperTarget),
      enableScheduledLaunch: this._getFeatureFlag(
        response.data.enableScheduledLaunch
      ),
      enableSurveyExternalId: this._getFeatureFlag(
        response.data.enableSurveyExternalId
      ),
      enableNightcrawler: this._getFeatureFlag(
        response.data.enableNightcrawler
      ),
      enableDataQualityQues: this._getFeatureFlag(
        response.data.enableDataQualityQues
      ),
      enableSurveyDQBattery: this._getFeatureFlag(
        response.data.enableSurveyDQBattery
      ),
      advanceStats: this._getFeatureFlag(response.data.advanceStats),
      enableQuestionLibrary: this._getFeatureFlag(
        response.data.enableQuestionLibrary
      ),
      enableTrafficChannels: this._getFeatureFlag(
        response.data.enableTrafficChannels
      ),
      enableInvoiceTab: this._getFeatureFlag(response.data.enableInvoiceTab),
      enablePriceRecomendation: this._getFeatureFlag(
        response.data.enablePriceRecomendation
      ),
      enableSupplierGrouping: this._getFeatureFlag(
        response.data.enableSupplierGrouping
      ),
      enableMarginMax: this._getFeatureFlag(response.data.enableMarginMax),
      enableClickToCalculate: this._getFeatureFlag(
        response.data.enableClickToCalculate
      ),
      enableAutoFieldManagement: this._getFeatureFlag(
        response.data.enableAutoFieldManagement
      ),
      enableMultiCountry: this._getFeatureFlag(
        response.data.enableMultiCountry
      ),
      enableManualProcessing: this._getFeatureFlag(
        response.data.enableManualProcessing
      ),
      enablePureScoreAndRD: this._getFeatureFlag(
        response.data.enablePureScoreAndRD
      ),
      enableViewByPM: this._getFeatureFlag(response.data.enableViewByPM),
      enableBasicToken: this._getFeatureFlag(response.data.enableBasicToken),
      enablePurePriceRates: this._getFeatureFlag(
        response.data.enablePurePriceRates
      ),
      enableBillingNumberRules: this._getFeatureFlag(
        response.data.enableBillingNumberRules
      ),
      enableMandatoryBillingNumber: this._getFeatureFlag(
        response.data.enableMandatoryBillingNumber
      ),
      enableTransactionTestingTool: this._getFeatureFlag(
        response.data.enableTransactionTestingTool
      ),
      enablePMRequired: this._getFeatureFlag(response.data.enablePMRequired),
      enableMultiCountrySingleProjectView: this._getFeatureFlag(
        response.data.enableMultiCountrySingleProjectView
      ),
      enableSegmentationClass: this._getFeatureFlag(
        response.data.enableSegmentationClass
      ),
      enableClassicSTR: this._getFeatureFlag(response.data.enableClassicSTR),
      enablePaceTool: this._getFeatureFlag(response.data.enablePaceTool),
      enableCreateSurveyV2: this._getFeatureFlag(
        response.data.enableCreateSurveyV2
      ),
      enableSimilarSurveys: this._getFeatureFlag(
        response.data.enableSimilarSurveys
      ),
      enableSearchAllMarketPlaceInventory: this._getFeatureFlag(
        response.data.enableSearchAllMarketPlaceInventory
      ),
      enableSupplierAllocationThrottling: this._getFeatureFlag(
        response.data.enableSupplierAllocationThrottling
      ),
      enableRYGSurveyScore: this._getFeatureFlag(
        response.data.enableRYGSurveyScore
      ),
      enableIntercomApp: this._getFeatureFlag(response.data.enableIntercomApp),
      enableAiDeliveryAssistance: this._getFeatureFlag(
        response.data.enableAiDeliveryAssistance
      ),
      enableNestingQuotaGrouping: this._getFeatureFlag(
        response.data.enableNestingQuotaGrouping
      ),
      enableNestingMultiQuota: this._getFeatureFlag(
        response.data.enableNestingMultiQuota
      ),
      enableRespondentStatusFilter: this._getFeatureFlag(
        response.data.enableRespondentStatusFilter
      ),
      enableScheduledPause: this._getFeatureFlag(
        response.data.enableScheduledPause
      ),
      enablePriceOverrideFeasibilityMultiplier: this._getFeatureFlag(
        response.data.enablePriceOverrideFeasibilityMultiplier
      ),
      enableTracker: this._getFeatureFlag(response.data.enableTracker),
      enableBypassLOIThreshold: this._getFeatureFlag(
        response.data.enableBypassLOIThreshold
      ),
      enableBackEndSortFilter: this._getFeatureFlag(
        response.data.enableBackEndSortFilter
      ),
      enableCustomSupplierAllocationDefault: this._getFeatureFlag(
        response.data.enableCustomSupplierAllocationDefault
      ),
      enableRangeQuotasDistribution: this._getFeatureFlag(
        response.data.enableRangeQuotasDistribution
      ),
      enableFMAStatusBasedAutoPause: this._getFeatureFlag(
        response.data.enableFMAStatusBasedAutoPause
      ),
      customExclusionPeriod: this._getFeatureFlag(
        response.data.customExclusionPeriod
      ),
      enablePermanentExclusion: this._getFeatureFlag(
        response.data.enablePermanentExclusion
      ),
      setPermanentExclusionOffByDefault: this._getFeatureFlag(
        response.data.setPermanentExclusionOffByDefault
      ),
    };
  }

  /**
   * Checks if a given feature is enabled for a specific company — accessible only by operators.
   *
   * @param feature - The name of the feature flag to check.
   * @param cmp - The company ID to check the feature flag against.
   * @returns `true` if the user is an operator **and** the feature is enabled for the company;
   *          otherwise returns `false`.
   *
   * Note: This function always returns `false` for non-operator users.
   */
  hasCompanyFeatureEnabled(
    feature: keyof AllBuyerConfigSettings,
    cmp: number
  ): boolean {
    if (this.userType !== 'operator') {
      return false;
    }

    return !!this.allBuyerConfigs[feature]?.includes(cmp);
  }

  private async _updateBuyerConfigSettings(
    buyerCompanyId: number
  ): Promise<void> {
    const buyerConfig = await this._getBuyerConfigSettings(buyerCompanyId);
    const companyConfig = await this._getCompanyConfigSettings(buyerCompanyId);
    const currencyExchange = await this._getCurrencyExchange(
      companyConfig.fx || CURRENCY_CODE.US
    );
    this._setState({
      buyerConfig,
      companyConfig,
      currencyExchange,
    });
  }

  private async _getBuyerConfigSettings(
    companyId: number
  ): Promise<BuyerConfig> {
    const buyerConfigResponse: GetBuyerConfigResponse =
      await this._buyerConfigService.getBuyerConfig().toPromise();
    const buyerConfig: BuyerConfig = this._getConfigSetting(
      buyerConfigResponse,
      companyId
    );
    return buyerConfig;
  }

  private async _getCompanyConfigSettings(companyId: number) {
    const {
      company: [companyObj],
    }: CompanyResponse = await this._companyService
      .getCompany(companyId)
      .toPromise();
    return companyObj;
  }

  private async _getCurrencyExchange(fx: number): Promise<CurrencyExchange> {
    if (fx === CURRENCY_CODE.US) {
      return this._defaultCurrencyExchange;
    }
    const response = await this._currencyService
      .getCurrencyExchange(fx)
      .toPromise();
    return response.currencies;
  }

  // This function is specific to marketplace components inherited from component library
  get getMarketplaceAuth(): AuthStateInterface {
    return {
      token: this._state.token,
      user: this._state.user,
      userType: this._state.userType,
      buyerConfig: this._state.buyerConfig,
      churnZeroKey: this._state.churnZeroKey,
      serviceBuyer: this._state.serviceBuyer,
      selectedMultiBuyerCompany: this._state.selectedMultiBuyerCompany,
      companyConfig: this.companyConfig as CompanyResponseObject,
      allBuyerConfigs: this._state.allBuyerConfigs,
      accessControlConfig: this._state.accessControlConfig,
    };
  }

  public email(): string {
    return this.user?.eml || 'No user logged in';
  }

  /**
   * OIDC Hydration - Integrates massive session object into the Angular state
   */
  public hydrateOidcSession(sessionData: any, rememberUser: boolean = true) {
    this._rememberUser = rememberUser;
    const userType = this.getUserType(sessionData.user);

    const userWithOidc = {
      ...sessionData.user,
      isOidc: true,
    };

    const accessControlConfig = sessionData.accessControlConfig
      ? AccessConfig.fromApiResponse(sessionData.accessControlConfig)
      : AccessConfig.default();

    this._setState({
      token: sessionData.token,
      user: userWithOidc,
      userType: userType,
      buyerConfig: sessionData.buyerConfig,
      companyConfig: sessionData.companyConfig,
      rememberUser: rememberUser,
      churnZeroKey: sessionData.churnZeroKey,
      allBuyerConfigs:
        sessionData.allBuyerConfigs || this._allBuyerConfigSettings,
      currencyExchange: sessionData.currencyExchange || this._currencyExchange,
      accessControlConfig,
    });

    if (rememberUser) {
      localStorage.setItem('ps_token', sessionData.token);
      localStorage.setItem('ps_user', JSON.stringify(userWithOidc));
    }

    // Track OIDC Login as a standard Login event
    this._churnZeroService.sendEvent(
      ChurnZeroConsts.events.login,
      ChurnZeroConsts.eventDescriptions.login
    );

    this._appcuesService.sendEvent(AppcuesConsts.events.login, {
      description: AppcuesConsts.eventDescriptions.login,
    });

    this._newrelic?.setUserId(`${sessionData.user.id}`);
    this._startIntercom();
  }

  /**
   * Handles all OIDC-specific redirection and state logic
   */
  private _handleOidcSessionEnd(): void {
    const logoutUrl = new URL(
      `${environment.pureSpectrum.authUrl}/oidc/session/end`
    );

    // Use the OIDC standard param to ensure the user comes back to oidc page
    const returnTo = `${window.location.origin}/oidc`;
    logoutUrl.searchParams.set('client_id', 'purespectrum');
    logoutUrl.searchParams.set('post_logout_redirect_uri', returnTo);

    window.location.assign(logoutUrl.toString());
  }

  private _setFullStoryIdentity(res: LoginResponse): void {
    if (!environment.production) {
      return;
    }
    FullStory('setIdentity', {
      uid: res.user.id.toString(),
      properties: {
        displayName: res.user.usrName,
        email: res.user.eml,
      },
    });
    return;
  }
}
